import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { OrderStore, OrderPlatformProduct, MembershipTierSelection } from "./order-store";
import type { Dataset } from "@/types/datasets.types";
import { getEffectiveMonthlyPrice } from "@/utils/platform-product-card-utils";

const getCurrentTimeInSecondsRounded = () => {
  return Math.floor(Date.now() / 1000);
};

const STORAGE_KEY = "cart-storage";

function withoutEdgeProducts(products: OrderPlatformProduct[]) {
  return products.filter(item => item.product.type !== "edge");
}

function resolveEdgeCartItem(
  platformProduct: OrderPlatformProduct,
  membershipTier: MembershipTierSelection | null
): OrderPlatformProduct | null {
  if (!membershipTier) return null;
  const tier = platformProduct.product.tiers.find(t => t.slug === membershipTier.tierSlug);
  if (!tier?.pricing) return null;
  const amount = Number(tier.pricing.amount);
  const listPrice = Number.isFinite(amount) ? amount : 0;
  return {
    ...platformProduct,
    tierId: tier.id,
    tierName: tier.displayName ?? tier.name,
    monthlyPrice: getEffectiveMonthlyPrice(platformProduct.product, listPrice),
  };
}

export const useOrder = create<OrderStore>()(
  persist(
    (set, get) => ({
      items: [],
      platformProducts: [],
      membershipTier: null,
      pendingPlatformUpgrade: undefined,
      orderCreatedTimestamp: undefined,
      plan: undefined,
      customerAddress: undefined,
      useCase: undefined,
      setUseCase: useCase => {
        set({ useCase });
      },
      addPlan: plan => {
        set(state => {
          // Filter out datasets that are not active on the new plan
          const filteredItems = state.items.filter(dataset => {
            const datasetPlan = dataset.plans?.find((p: { planId: string; active?: boolean }) => p.planId === plan.id);
            return datasetPlan?.active === true;
          });

          return {
            plan,
            items: filteredItems,
            platformProducts: [],
            membershipTier: null,
            pendingPlatformUpgrade: undefined,
            orderCreatedTimestamp: state.orderCreatedTimestamp ?? getCurrentTimeInSecondsRounded(),
          };
        });
      },
      clearPlan: () => {
        set(state => {
          if (state.items.length === 0) {
            return {
              plan: undefined,
              orderCreatedTimestamp: undefined,
            };
          }
          return {
            plan: undefined,
          };
        });
      },

      addItem: (dataset: Dataset) => {
        set(state => {
          const existingItem = state.items.find(item => item.id === dataset.id);

          if (existingItem) {
            return state;
          }

          return {
            items: [...state.items, { ...dataset }],
            platformProducts: [],
            membershipTier: null,
            pendingPlatformUpgrade: undefined,
            orderCreatedTimestamp: state.orderCreatedTimestamp ?? getCurrentTimeInSecondsRounded(),
          };
        });
      },

      removeItem: (datasetId: string) => {
        set(state => {
          const existingItem = state.items.filter(item => item.id !== datasetId);
          if (existingItem.length === 0 && !state.plan) {
            return {
              items: existingItem,
              orderCreatedTimestamp: undefined,
            };
          }
          return {
            items: existingItem,
          };
        });
      },

      updateQuantity: (datasetId: string, quantity: number) => {
        set(state => ({
          items: state.items.map(item => (item.id === datasetId ? { ...item, quantity } : item)),
        }));
      },

      clearCart: () => {
        set({
          items: [],
          plan: undefined,
          platformProducts: [],
          membershipTier: null,
          pendingPlatformUpgrade: undefined,
          orderCreatedTimestamp: undefined,
          promotionCode: undefined,
          promotionCodeTerms: undefined,
          purchaseOrderNumber: undefined,
        });
      },

      setMembershipTier: (selection: MembershipTierSelection | null) => {
        set(state => {
          const tierSlugChanged =
            selection?.tierSlug != null &&
            state.membershipTier?.tierSlug != null &&
            selection.tierSlug !== state.membershipTier.tierSlug;
          const cleared = selection === null;
          const nextProducts =
            tierSlugChanged || cleared ? withoutEdgeProducts(state.platformProducts) : state.platformProducts;
          return {
            membershipTier: selection,
            platformProducts: nextProducts,
          };
        });
      },

      getItemsTotal: actualPlan => {
        if (!actualPlan) {
          return 0;
        }
        return get().items.reduce((sum, dataset) => {
          const datasetPlan = dataset.plans.find(
            (plan: { planId: string; active?: boolean; monthlyPrice?: number }) => plan.planId === actualPlan?.id
          );
          // Only count active datasets
          if (!datasetPlan?.active) {
            return sum;
          }
          return sum + (datasetPlan?.monthlyPrice || 0);
        }, 0);
      },
      orderInProgress: () => {
        return (
          get().plan !== undefined ||
          get().items.length > 0 ||
          get().platformProducts.length > 0 ||
          get().pendingPlatformUpgrade !== undefined
        );
      },
      addPlatformProduct: (platformProduct: OrderPlatformProduct) => {
        set(state => {
          const productType = platformProduct.product.type;
          let nextItem = platformProduct;

          if (productType === "edge") {
            const resolved = resolveEdgeCartItem(platformProduct, state.membershipTier);
            if (!resolved) return state;
            nextItem = resolved;
          }

          let nextMembershipTier = state.membershipTier;
          if (productType === "tiered") {
            const tier = platformProduct.product.tiers.find(t => t.id === platformProduct.tierId);
            if (tier) {
              const gatesEdgePricing =
                platformProduct.product.isMembershipProduct ||
                state.platformProducts.some(
                  item => item.product.type === "edge" && item.product.parentProductId === platformProduct.productId
                ) ||
                state.membershipTier?.productId === platformProduct.productId;
              if (gatesEdgePricing) {
                nextMembershipTier = {
                  productId: platformProduct.productId,
                  tierId: tier.id,
                  tierSlug: tier.slug,
                };
              }
            }
          }

          const tierSlugChanged =
            state.membershipTier?.tierSlug != null &&
            nextMembershipTier?.tierSlug != null &&
            state.membershipTier.tierSlug !== nextMembershipTier.tierSlug;

          let nextProducts = state.platformProducts.filter(item => item.productId !== nextItem.productId);
          if (tierSlugChanged) {
            nextProducts = withoutEdgeProducts(nextProducts);
          }

          return {
            platformProducts: [...nextProducts, nextItem],
            membershipTier: nextMembershipTier,
            pendingPlatformUpgrade: undefined,
            plan: undefined,
            items: [],
            orderCreatedTimestamp: state.orderCreatedTimestamp ?? getCurrentTimeInSecondsRounded(),
          };
        });
      },
      removePlatformProduct: productId => {
        set(state => {
          const removed = state.platformProducts.find(item => item.productId === productId);
          let nextProducts = state.platformProducts.filter(item => item.productId !== productId);
          let nextMembershipTier = state.membershipTier;

          if (
            removed?.product.type === "tiered" &&
            (removed.product.isMembershipProduct ||
              state.platformProducts.some(
                item => item.product.type === "edge" && item.product.parentProductId === removed.productId
              )) &&
            state.membershipTier?.productId === productId
          ) {
            nextMembershipTier = null;
            nextProducts = withoutEdgeProducts(nextProducts);
          }

          return {
            platformProducts: nextProducts,
            membershipTier: nextMembershipTier,
            orderCreatedTimestamp:
              nextProducts.length === 0 && !nextMembershipTier ? undefined : state.orderCreatedTimestamp,
          };
        });
      },
      setPendingPlatformUpgrade: upgrade => {
        set(state => ({
          pendingPlatformUpgrade: upgrade,
          platformProducts: [],
          membershipTier: null,
          plan: undefined,
          items: [],
          orderCreatedTimestamp: state.orderCreatedTimestamp ?? getCurrentTimeInSecondsRounded(),
        }));
      },
      clearPendingPlatformUpgrade: () => {
        set(state => ({
          pendingPlatformUpgrade: undefined,
          orderCreatedTimestamp:
            state.items.length === 0 && !state.plan && state.platformProducts.length === 0
              ? undefined
              : state.orderCreatedTimestamp,
        }));
      },
      setCurrentCustomerAddress: address => {
        set({ customerAddress: address });
      },
      promotionCode: undefined,
      promotionCodeTerms: undefined,
      setPromotionCode: (code, terms) => {
        set({ promotionCode: code, promotionCodeTerms: terms });
      },
      purchaseOrderNumber: undefined,
      setPurchaseOrderNumber: po => {
        set({ purchaseOrderNumber: po });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        items: state.items,
        plan: state.plan,
        platformProducts: state.platformProducts,
        membershipTier: state.membershipTier,
        pendingPlatformUpgrade: state.pendingPlatformUpgrade,
        customerAddress: state.customerAddress,
        promotionCode: state.promotionCode,
        promotionCodeTerms: state.promotionCodeTerms,
        // purchaseOrderNumber is intentionally NOT persisted: a PO is per-order accounting
        // data and must not rehydrate from localStorage onto an unrelated later checkout.
      }),
      version: 3,
      migrate: persistedState => {
        const state = persistedState as Partial<OrderStore>;
        return {
          ...state,
          items: state.items ?? [],
          platformProducts: state.platformProducts ?? [],
          membershipTier: state.membershipTier ?? null,
          pendingPlatformUpgrade: state.pendingPlatformUpgrade,
        };
      },
      skipHydration: true,
      // Handle rehydration and ensure orderCreatedTimestamp is set
      onRehydrateStorage: () => {
        return (rehydratedState, error) => {
          if (!error && rehydratedState) {
            // If there are items or a plan in the cart but no timestamp, set it
            if (
              (rehydratedState.items?.length > 0 ||
                rehydratedState.plan ||
                rehydratedState.platformProducts?.length > 0 ||
                rehydratedState.pendingPlatformUpgrade) &&
              !rehydratedState.orderCreatedTimestamp
            ) {
              // Use the store's setState to update the timestamp
              useOrder.setState({
                orderCreatedTimestamp: getCurrentTimeInSecondsRounded(),
              });
            }
          }
        };
      },
    }
  )
);

/** Clears any persisted checkout promotion code (localStorage via cart-storage). */
export function clearStoredPromotionCode() {
  useOrder.getState().setPromotionCode(undefined, undefined);
}
